import IntroComponent from '../components/intro.components';
import InvestIcon from '../../../assets/invest-icon.svg';
import InformIcon from '../../../assets/inform-dark-green-icon.svg';
import LeafIcon from '../../../assets/leaf-icon.svg';
import SeedIcon from '../../../assets/seed-icon.svg';
import RightArrow from '../../../assets/arrow-right-black.svg';
import { getArticleInvestorSummarize, getInvestorsPointList } from '../../../apis/article.api';
import { useQuery } from '@tanstack/react-query';
import { TooltipComponent } from '../../Tooltip';
import { useEffect, useState } from 'react';
import { InvestorsTableComponent } from '../../Modal/InvestorDetail/investors-table.component';
import RankBadge from '../../RankBadge';
import { Box, Text } from '@chakra-ui/react';
import ImageComponent from '../../Common/Image';
import AvatarDefault from '../../../assets/medium_default_optimized.png';

const ArticleInvestorsComponent = ({ articleId }) => {
  const [dataTable, setDataTable] = useState([]);
  const [pagination, setPagination] = useState({
    limit: 4,
    page: 1,
    sortBy: undefined,
    sort: undefined,
    search: undefined
  });

  const params = {
    ...pagination
  };

  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined)
  );

  const getStylistTooltipSeedToInvest = () => {
    return (
      <div className="m-2">
        <div className="flex justify-center">
          <div className="flex flex-row gap-1">
            <img src={LeafIcon} alt="" />
            <b>Received Twigs</b>
            <img src={RightArrow} alt="" />
            <img src={SeedIcon} alt="" />
            <b>Seeds</b>
          </div>
        </div>
        <p>
          The amount of Seeds available for you to invest is the total number of Twigs you've been
          awarded by others, minus the number of Seeds you've already invested in articles.
        </p>
        <b>Twigs received - Seeds invested = Available Seeds for investing</b>
      </div>
    );
  };

  const {
    data: investmentPointHistory,
    isFetching,
    refetch
  } = useQuery({
    queryKey: ['getInvestorsPointList', filteredParams],
    queryFn: () => getInvestorsPointList(articleId, filteredParams)
  });

  const { data: investorSummarize } = useQuery({
    queryKey: ['getArticleInvestorSummarize', filteredParams],
    queryFn: () => getArticleInvestorSummarize({ articleId })
  });

  useEffect(() => {
    if (investmentPointHistory) {
      setDataTable(investmentPointHistory);
    }
  }, [investmentPointHistory]);

  return (
    <div className="flex flex-col gap-6">
      <IntroComponent
        title="Investors on this Article"
        des="View investors and investments made on this article"
        titleSize="sm"
        className="mt-5"
      />

      <div className="px-4 md:px-0 flex items-center gap-5 flex-col md:flex-row">
        <div className="bg-white p-6 rounded-[12px] border border-05-seasalt-white shadow-[0px_4px_4px_0px_#F2F3F780] w-full md:w-fit">
          <div className="flex items-center">
            <img src={InvestIcon} className="w-5 h-5" alt="" />
            <p className="font-[800] ml-[5px] text-sm">Investors on this Article</p>
          </div>
          <div className="mt-6 flex flex-wrap md:flex-nowrap gap-4">
            <div className="flex flex-col gap-2 w-full md:w-auto pr-4 border-b md:border-b-0 border-r-0 md:border-r border-[#E6E7EB]">
              <div className="flex gap-1">
                <p className="text-05-growth-green-900 font-[800] text-sm">
                  Total Seeds invested in this article
                </p>
                <TooltipComponent
                  maxW="420px"
                  placement="top"
                  Icon={InformIcon}
                  className="w-5 h-5"
                  description={getStylistTooltipSeedToInvest()}
                />
              </div>
              <p className="text-2xl text-growth-green-900 font-[800] flex items-center gap-1">
                {investorSummarize?.totalUserPointsInvested || 0}
                <span className="text-sm font-[400]">
                  {'Seed' + (investorSummarize?.totalUserPointsInvested > 1 ? 's' : '')}
                </span>
              </p>
            </div>
            <div className="flex flex-col gap-2 w-full md:w-auto pr-4 border-b md:border-b-0 border-r-0 md:border-r border-[#E6E7EB]">
              <p className="text-05-growth-green-900 font-[800] text-sm">Total investors</p>
              <p className="text-2xl text-growth-green-900 font-[800] flex items-center gap-1">
                {investorSummarize?.totalInvestors || 0}
                <span className="text-sm font-[400]">
                  {'Investor' + (investorSummarize?.totalInvestors > 1 ? 's' : '')}
                </span>
              </p>
            </div>
            <div className="flex flex-col gap-2 w-full md:w-auto pr-4 border-b md:border-b-0 border-[#E6E7EB]">
              <div className="flex gap-1">
                <p className="text-05-growth-green-900 font-[800] text-sm">Top investors</p>
              </div>

              <div className="text-2xl text-growth-green-900 font-[800] mt-auto">
                {investorSummarize?.topInvestors?.length > 0 ? (
                  <div className="flex flex-rows gap-4">
                    {investorSummarize.topInvestors.slice(0, 2).map((investor, index) => (
                      <Box
                        key={index}
                        className="grid grid-cols-[40px_1fr_auto] items-center gap-2 min-w-0"
                      >
                        {/* Fixed column for avatar */}
                        <ImageComponent
                          rounded="12px"
                          w="40px"
                          h="40px"
                          src={investor?.imgUrl || AvatarDefault}
                          alt="cover"
                        />

                        {/* Flexible column for username */}
                        <Text
                          className="truncate text-gray-500 min-w-0"
                          fontWeight={600}
                          fontSize="sm"
                          title={investor?.firstName}
                          _hover={{ textDecoration: 'underline' }}
                        >
                          {'@' + investor?.username}
                        </Text>

                        {/* Fixed column for badge */}
                        <RankBadge
                          textSize="text-xs md:text-sm"
                          iconSize="w-[16px] h-[16px]"
                          rank={investor?.currentRank}
                          hiddenRankText={true}
                          isBlur={false}
                        />
                      </Box>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <p className="invisible">0</p>
                    <span className="text-sm font-[400] ml-[-18px]">No investors yet</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <InvestorsTableComponent
        dataTable={dataTable?.data?.articleInvestors}
        totalPage={dataTable.totalPage}
        pagination={pagination}
        setPagination={setPagination}
        refetch={refetch}
        isFetching={isFetching}
      />
    </div>
  );
};

export default ArticleInvestorsComponent;
